import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { useSocket } from './SocketContext';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../config/api';
import i18n from '../i18n/config';
import { playNotificationSound } from '../utils/audioCues';
import {
  hasNotificationApi,
  shouldUseDesktopNotifications,
  supportsNotificationActions,
  supportsNotificationVibrate
} from '../utils/runtimeEnvironment';

export interface NotificationData {
  id?: number | string;
  type: string;
  message: string;
  title?: string;
  auctionId?: string | number;
  data?: any;
  timestamp?: Date;
  read?: boolean;
  thumbnailUrl?: string | null;
  createdAt?: string;
}

export interface NotificationContextType {
  addNotification: (data: NotificationData) => void;
  notifications: NotificationData[];
  unreadCount: number;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  clearAll: () => void;
  requestPermission: () => Promise<boolean>;
  isPermissionGranted: boolean;
  fetchNotifications: () => Promise<void>;
  loading: boolean;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { socket, isConnected } = useSocket();
  const { user, token } = useAuth();

  const showBrowserNotification = useCallback((data: NotificationData) => {
    if (!isPermissionGranted || !shouldUseDesktopNotifications()) return;

    const title = data.title || i18n.t('notifications.browserTitle');
    const options: NotificationOptions = {
      body: data.message,
      icon: '/1757915640770_bq3j5.png',
      badge: '/1757915640770_bq3j5.png',
      tag: data.type,
      requireInteraction: true,
      data: {
        ...data.data,
        auctionId: data.auctionId,
        timestamp: Date.now()
      }
    };

    if (supportsNotificationVibrate()) {
      options.vibrate = [200, 100, 200];
    }

    // أزرار الإشعار غير مدعومة على iOS — تسبب أخطاء في بعض WebViews
    if (
      supportsNotificationActions() &&
      (data.type === 'NEW_BID' || data.type === 'BID_OUTBID')
    ) {
      options.actions = [
        {
          action: 'view_auction',
          title: i18n.t('notifications.viewAuction'),
          icon: '/1757915640770_bq3j5.png'
        },
        {
          action: 'dismiss',
          title: i18n.t('notifications.dismiss'),
          icon: '/1757915640770_bq3j5.png'
        }
      ];
    }

    try {
      const notification = new Notification(title, options);
      
      notification.onclick = () => {
        window.focus();
        if (data.auctionId) {
          window.location.href = `/auction/${data.auctionId}`;
        }
        notification.close();
      };

      // إغلاق الإشعار تلقائياً بعد 10 ثوان
      setTimeout(() => {
        notification.close();
      }, 10000);
    } catch (error) {
      // تجاهل أخطاء الإشعارات في production
      if (process.env.NODE_ENV === 'development') {
        console.error('Error showing notification:', error);
      }
    }
  }, [isPermissionGranted]);

  // جلب الإشعارات من API
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Normalize: API returns isRead, frontend expects read
        const normalized = (data.notifications || []).map((n: NotificationData & { isRead?: boolean }) => ({
          ...n,
          read: n.read ?? n.isRead ?? false
        }));
        setNotifications(normalized);
        setUnreadCount(data.unreadCount ?? normalized.filter((n: NotificationData) => !n.read).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const addNotification = useCallback((data: NotificationData) => {
    const newNotification = {
      ...data,
      timestamp: new Date()
    };

    setNotifications(prev => [newNotification, ...prev.slice(0, 49)]); // الاحتفاظ بآخر 50 إشعار
    setUnreadCount(prev => prev + 1);

    // عرض إشعار المتصفح
    showBrowserNotification(newNotification);

    // إشعار صوتي (Web Audio API - لا يحتاج ملف)
    try {
      playNotificationSound();
    } catch {
      // تجاهل الأخطاء
    }
  }, [showBrowserNotification]);

  // فحص حالة الأذونات عند التحميل
  useEffect(() => {
    checkNotificationPermission();
  }, []);

  // جلب الإشعارات مرة واحدة عند تسجيل الدخول (اعتماداً على user?.id و token فقط لتجنّب طلبات مكررة)
  useEffect(() => {
    if (user?.id != null && token) {
      fetchNotifications();
    }
  }, [user?.id, token, fetchNotifications]);

  // الاستماع للإشعارات من Socket.IO (يُفعّل عند الاتصال دون رسائل كونسول مزعجة)
  useEffect(() => {
    if (!socket || !isConnected) return;

    socket.on('new_notification', (data: NotificationData) => {
      addNotification(data);
    });

    return () => {
      socket.off('new_notification');
    };
  }, [socket, isConnected, addNotification]);

  const checkNotificationPermission = () => {
    if (hasNotificationApi()) {
      setIsPermissionGranted(Notification.permission === 'granted');
    } else {
      setIsPermissionGranted(false);
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    if (!shouldUseDesktopNotifications()) {
      return false;
    }
    if (!hasNotificationApi()) {
      console.log('هذا المتصفح لا يدعم الإشعارات');
      return false;
    }

    if (Notification.permission === 'granted') {
      setIsPermissionGranted(true);
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      setIsPermissionGranted(granted);
      
      if (granted) {
        // تسجيل Service Worker للإشعارات (مع مهلة قصيرة لتجنب التحميل اللانهائي)
        if ('serviceWorker' in navigator && shouldUseDesktopNotifications()) {
          try {
            await Promise.race([
              navigator.serviceWorker.ready,
              new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000))
            ]);
            console.log('Service Worker جاهز للإشعارات');
          } catch (error) {
            // تجاهل أخطاء Service Worker - الإذن مُمنوح ومهم
            if (process.env.NODE_ENV === 'development') {
              console.warn('Service Worker غير جاهز بعد:', error);
            }
          }
        }
      }
      
      return granted;
    }

    return false;
  };

  const markAsRead = async (notificationId: string) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => 
            String(notif.id) === String(notificationId) ? { ...notif, read: true } : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const notification = notifications.find(n => String(n.id) === String(notificationId));
        const wasUnread = notification && (notification.read !== true && (notification as any).isRead !== true);
        setNotifications(prev => prev.filter(notif => String(notif.id) !== String(notificationId)));
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const clearAll = async () => {
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/notifications`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      // Fallback to local clear
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  return (
    <NotificationContext.Provider value={{ 
      addNotification,
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      clearAll,
      requestPermission,
      isPermissionGranted,
      fetchNotifications,
      loading
    }}>
      {children}
    </NotificationContext.Provider>
  );
};